import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { fetchCompanyWeeklyTrackTime } from '../../../../states/containers/setting/companyTrackTimeSlice'
import { RootState } from '../../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../../../components/common/Tips'
import EditModal from '../../../components/setting/record/EditModal'
import ConfirmationModal from '../../../components/setting/record/ConfirmationModal'
import {
  fetchMemberWeeklyTrackTime,
  updateMembersWeeklyTrackTime,
  destroyMembersWeeklyTrackTime,
} from '../../../../states/containers/setting/memberWeeklyTrackTimeSlice'
import { sortBy } from 'lodash'
import { deleteMemberWeeklyTrackTimeRequest, patchMemberWeeklyTrackTimeRequest } from '../../../../states/types/setting'
import { compile } from '../../../../utils/search'
import { fetchAccessToken } from '../../../../states/containers/auth/loginSlice'

interface membersModal {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  contract: string | null
  profile_image: string | null
  change_authority: boolean
  weekly_track_times: weekly_track_time[]
}

interface weekly_track_time {
  week_number: number
  track_start_time: string | null
  track_stop_time: string | null
}

interface unCreateMembersModal {
  id: number
  name: string | null
  kana: string | null
  email: string | null
  code: string | null
  contract: string | null
  profile_image: string | null
}

interface EditModalState {
  display: boolean
  type: string
  editData: weekly_track_time[] | null
  targetMemberData: membersModal[] | null
  selectableMemberData: unCreateMembersModal[] | null
}

interface ConfirmationModalState {
  display: boolean
  type: string
  confirmationMemberData: membersModal[] | null
  callback: VoidFunction
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    companyWeeklyTrackTime,
    memberWeeklyTrackTime,
    login,
  } = useSelector((state: RootState) => state)

  const [membersInit, setMembersInit] = useState<membersModal[]>()
  const [members, setMembers] = useState<membersModal[]>()
  const [unCreateMembers, setUnCreateMembers] = useState<unCreateMembersModal[]>()
  const [companyWeeklyTrackTimes, setCompanyWeeklyTrackTimes] = useState<weekly_track_time[]>()
  const [checkedMemberIds, setCheckedMemberIds] = useState<number[]>([])
  const [editModalState, setEditModalState] = useState<EditModalState>({
    display: false,
    type: "",
    editData: null,
    targetMemberData: null,
    selectableMemberData: null,
  })
  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    confirmationMemberData: null,
    callback: () => {
      // none
    },
  })
  const [searchWord, setSearchWord] = useState<string>("")
  const [composition, setComposition] = useState<boolean>(false)
  const [sortType, setSortType] = useState<string>("")

  const openMemberEditModal = (ids: number[]) => {
    const tmp = members?.filter((v) => ids.includes(v.id)) || []
    setEditModalState({
      display: true,
      type: "updateMember",
      editData: tmp[0]?.weekly_track_times,
      targetMemberData: tmp || null,
      selectableMemberData: null,
    })
  }

  const openCompanyEditModal = () => {
    setEditModalState({
      display: true,
      type: "updateCompany",
      editData: companyWeeklyTrackTimes || null,
      targetMemberData: null,
      selectableMemberData: null,
    })
  }

  const closeEditModal = () => {
    setEditModalState({
      display: false,
      type: "",
      editData: null,
      targetMemberData: null,
      selectableMemberData: null,
    })
  }

  const openAddMemberModal = () => {
    setEditModalState({
      display: true,
      type: "createMember",
      editData: companyWeeklyTrackTimes || null,
      targetMemberData: null,
      selectableMemberData: unCreateMembers || null,
    })
  }

  const destroyMember = (id: number) => {
    const member_ids = [id]
    const params: deleteMemberWeeklyTrackTimeRequest = {
      member_ids: member_ids,
    }
    dispatch(
      destroyMembersWeeklyTrackTime(
        params,
        () => {
          alert.success("個別設定を削除しました")
          dispatch(fetchAccessToken(
            () => { /* */ },
            () => { /* */ },
          ))
        },
      ),
    )
  }

  const changeRight = (id: number) => {
    const member_ids = [id]
    const targetMember = members?.find((v) => v.id === id)
    const change_authority = targetMember?.change_authority
    const params: patchMemberWeeklyTrackTimeRequest = {
      member_ids: member_ids,
      change_authority: !change_authority,
    }
    dispatch(
      updateMembersWeeklyTrackTime(
        params,
        () => {
          alert.success("編集許可を変更しました")
          dispatch(fetchAccessToken(
            () => { /* */ },
            () => { /* */ },
          ))
        },
      ),
    )
  }

  const openConfirmationModal = (id: number, type: string) => {
    const tmp = members?.filter((v) => v.id === id)
    if (type === "destroy") {
      const callback = () => destroyMember(id)
      setConfirmationModalState({
        display: true,
        type: type,
        confirmationMemberData: tmp || null,
        callback: callback,
      })
    } else {
      const callback = () => changeRight(id)
      setConfirmationModalState({
        display: true,
        type: type,
        confirmationMemberData: tmp || null,
        callback: callback,
      })
    }
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      display: false,
      type: "",
      confirmationMemberData: null,
      callback: () => {
        // none
      },
    })
  }

  const clickCheckbox = (memberId: number) => {
    if (checkedMemberIds.includes(memberId)) {
      setCheckedMemberIds(checkedMemberIds.filter((v) => v !== memberId))
    } else {
      setCheckedMemberIds([...checkedMemberIds, memberId])
    }
  }

  const reset = () => {
    setSearchWord("")
    setSortType("")
    setMembers(membersInit)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const search = (originalList: membersModal[] | undefined) => {
    if (!searchWord) {
      reset()
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name + member.kana + member.email + member.contract + member.code))
    }) as membersModal[]
    setMembers(sortBy(list, 'id'))
  }

  const sortMembers = (key: string, type: string) => {
    const tmpList = sortBy(members, key)
    if (type === "desc") {
      return setMembers(tmpList.reverse())
    }
    return setMembers(tmpList)
  }

  const sortBlock = (label: string, type: string) => (
    <>
      {
        sortType === `${type}_-_desc` && (
          <div
            className="sort_block"
            onClick={() => setSortType(`${type}_-_asc`)}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort-amount-down']} />
            </span>
          </div>
        )
      }
      {
        sortType === `${type}_-_asc` && (
          <div
            className="sort_block"
            onClick={() => setSortType("")}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort-amount-down-alt']} />
            </span>
          </div>
        )
      }
      {
        !sortType.includes(type) && (
          <div
            className="sort_block"
            onClick={() => setSortType(`${type}_-_desc`)}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort']} />
            </span>
          </div>
        )
      }
    </>
  )

  const displayTime = (value: {week_number: number, track_start_time: string | null, track_stop_time: string | null}) => {
    if ((value.track_start_time === value.track_stop_time) && (value.track_start_time !== null && value.track_stop_time !== null)) {
      return (
        <Tips
          description="計測しない"
          className=""
          icon={false}
        >
          <div style={{ color: "#FF0000", fontSize: "22px" }}>
            <FontAwesomeIcon icon={['fas', 'times']} />
          </div>
        </Tips>
      )
    }
    if ((value.track_start_time === null) && (value.track_stop_time === null)) {
      return (
        <Tips
          description="終日計測する"
          className=""
          icon={false}
        >
          <div style={{ color: "#0f9655", fontSize: "22px" }}>
            <FontAwesomeIcon icon={['fas', 'play-circle']} />
          </div>
        </Tips>
      )
    }
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "2px",
        }}
      >
        <Tips
          description="計測制限あり"
          className=""
          icon={false}
        >
          <div style={{ color: "#777777", fontSize: "16px" }}>
            <FontAwesomeIcon icon={['far', 'clock']} />
          </div>
        </Tips>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "2px",
          }}
        >
          <div>
            {value.track_start_time ? value.track_start_time.substring(0, 5) : ""}
          </div>
          <div>
            ~
          </div>
          <div>
            {value.track_stop_time ? value.track_stop_time.substring(0, 5) : ""}
          </div>
        </div>
      </div>
    )
  }

  const membersList = (member_array: membersModal[]) => (
    member_array && member_array.map((v) => (
      <tr key={v.id}>
        <td>
          <div
            role="none"
            onClick={() => clickCheckbox(v.id)}
            className={`check_box ${checkedMemberIds.includes(v.id) ? "checked" : ""}`}
          >
            {
              checkedMemberIds.includes(v.id) ? (
                <FontAwesomeIcon icon={['far', 'check-square']} />
              ) : (
                <FontAwesomeIcon icon={['far', 'square']} />
              )
            }
          </div>
        </td>
        <td>
          <div className="track_setting">
            <Tips
              description="編集"
              className=""
              icon={false}
            >
              <button
                onClick={() => openMemberEditModal([v.id])}
                className="edit"
                type="button"
              >
                <FontAwesomeIcon icon={['fas', 'tools']} />
              </button>
            </Tips>
            <Tips
              description="個別設定を削除"
              className=""
              icon={false}
            >
              <button
                onClick={() => openConfirmationModal(v.id, "destroy")}
                className="delete"
                type="button"
              >
                <FontAwesomeIcon icon={['fas', 'trash-alt']} />
              </button>
            </Tips>
          </div>
        </td>
        <td>
          <div className="authority_area">
            <div
              role="none"
              className={`switch_outer ${v.change_authority ? "active" : ""}`}
              onClick={() => openConfirmationModal(v.id, "change_authority")}
            >
              <div className={`toggle_slider ${v.change_authority ? "active" : ""}`} />
            </div>
          </div>
        </td>
        <td>
          <div className="track_table_profile">
            {
              v.profile_image ? (
                <img alt="profile" src={v.profile_image} className="profile_icon" />
              ) : (
                <img
                  alt=""
                  src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                  className="profile_icon"
                />
              )
            }
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div>
                {v.name}
              </div>
              <div>
                { v.code ? `(${v.code})` : "" }
              </div>
            </div>
          </div>
        </td>
        {
          v.weekly_track_times.map((w) => (
            <td
              key={w.week_number}
              style={{
                padding: "8px 4px",
              }}
            >
              {displayTime(w)}
            </td>
          ))
        }
      </tr>
    ))
  )

  const buildList = useMemo(() => members && membersList(members), [members, checkedMemberIds])

  const legend = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        padding: "8px 16px",
        border: "solid 1px #CCCCCC",
        backgroundColor: "#FAFAFA",
        marginBottom: "8px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          color: "#666666",
        }}
      >
        凡例
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "right",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#0f9655" }}>
            <FontAwesomeIcon icon={['fas', 'play-circle']} />
          </div>
          <div>
            :
          </div>
          終日計測する
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#FF0000" }}>
            <FontAwesomeIcon icon={['fas', 'times']} />
          </div>
          <div>
            :
          </div>
          計測しない
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#777777" }}>
            <FontAwesomeIcon icon={['far', 'clock']} />
          </div>
          <div>
            :
          </div>
          計測制限あり
        </div>
      </div>
    </div>
  )

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    if (!companyWeeklyTrackTime.weeklyTrackTimes.length) {
      dispatch(
        fetchCompanyWeeklyTrackTime(),
      )
    }
    dispatch(
      fetchMemberWeeklyTrackTime(
        { only_oneself: false },
      ),
    )
  }, [login.accessToken, commonPart.company])

  useEffect(() => {
    if (!memberWeeklyTrackTime.member_list) return
    if (!searchWord) {
      setMembers(memberWeeklyTrackTime.member_list)
      setUnCreateMembers(memberWeeklyTrackTime.uncreate_member_list)
    } else {
      search(memberWeeklyTrackTime.member_list)
    }
    setMembersInit(sortBy(memberWeeklyTrackTime.member_list, 'id'))
  }, [memberWeeklyTrackTime.member_list])

  useEffect(() => {
    if (!companyWeeklyTrackTime.weeklyTrackTimes) return
    setCompanyWeeklyTrackTimes(companyWeeklyTrackTime.weeklyTrackTimes)
  }, [companyWeeklyTrackTime.weeklyTrackTimes])

  useEffect(() => {
    if (members === undefined) return
    if (!sortType) {
      setMembers(sortBy(members, 'id'))
      return
    }
    const [key, type] = sortType.split("_-_")
    sortMembers(key, type)
  }, [sortType])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t("setting.measurement.title")}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t("setting.measurement.title")}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>{t("setting.measurement.title")}</h1>
      </section>
      <section className="page_description">
        <p>
          Qaseeで計測する時間を設定することができます。
        </p>
        <p>
          メンバーを指定して個別に設定することもできます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="header_area measurement">
              <div className="title">会社の基本設定</div>
              <button
                type="button"
                className="edit_button"
                onClick={() => openCompanyEditModal()}
              >
                会社の基本設定を編集
              </button>
            </div>
            {legend}
            <table className="company_track_table">
              <thead>
                <tr>
                  <th style={{ color: "#FF0000" }}>日曜日</th>
                  <th>月曜日</th>
                  <th>火曜日</th>
                  <th>水曜日</th>
                  <th>木曜日</th>
                  <th>金曜日</th>
                  <th style={{ color: "#317AB7" }}>土曜日</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {
                    companyWeeklyTrackTimes && companyWeeklyTrackTimes?.map((v) => (
                      <td key={v.week_number}>
                        {displayTime(v)}
                      </td>
                    ))
                  }
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="header_area measurement">
              <div className="title">メンバーの計測時間</div>
              <div className="edit_button_area">
                <div className="search_area">
                  <input
                    type="text"
                    onChange={(event) => setSearchWord(event.target.value)}
                    onBlur={() => search(membersInit)}
                    onKeyDown={(event) => keyDown(event)}
                    onCompositionStart={() => setComposition(true)}
                    onCompositionEnd={() => setComposition(false)}
                    value={searchWord}
                    placeholder="キーワードでメンバー検索"
                  />
                  {
                    searchWord && (
                      <button type="button" className="reset" onClick={reset}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                <button
                  type="button"
                  className="edit_button"
                  onClick={openAddMemberModal}
                >
                  メンバーの追加
                </button>
                <button
                  type="button"
                  className={`edit_button ${checkedMemberIds.length === 0 ? "checked" : ""}`}
                  onClick={() => openMemberEditModal(checkedMemberIds)}
                >
                  チェックしたメンバーをまとめて編集
                </button>
              </div>
            </div>
            {legend}
            <table className="weekly_track_table">
              <thead>
                <tr className="total_table_header">
                  <th rowSpan={2}>
                    <div
                      className={`check_box ${checkedMemberIds.length === members?.length ? "checked" : ""}`}
                    >
                      {
                        checkedMemberIds.length === members?.length ? (
                          <FontAwesomeIcon icon={['far', 'check-square']} onClick={() => setCheckedMemberIds([])} />
                        ) : (
                          <FontAwesomeIcon icon={['far', 'square']} onClick={() => setCheckedMemberIds(members ? members.map((v) => v.id) : [])} />
                        )
                      }
                    </div>
                  </th>
                  <th rowSpan={2}>操作</th>
                  <th rowSpan={2} style={{ whiteSpace: "nowrap" }}>
                    <Tips
                      description="自分自身で計測時間設定を変更することができるようになります"
                      className=""
                    >
                      <>
                        編集許可
                      </>
                    </Tips>
                  </th>
                  <th rowSpan={2}>
                    {sortBlock("名前", "name")}
                  </th>
                  <th colSpan={7}>計測時間</th>
                </tr>
                <tr className="week_header">
                  <th style={{ color: "#FF0000" }}>日曜日</th>
                  <th>月曜日</th>
                  <th>火曜日</th>
                  <th>水曜日</th>
                  <th>木曜日</th>
                  <th>金曜日</th>
                  <th style={{ color: "#317AB7" }}>土曜日</th>
                </tr>
              </thead>
              <tbody>
                {buildList}
              </tbody>
            </table>
          </div>
        </div>
        {/* 編集モーダル */}
        <EditModal
          editModalState={editModalState}
          closeCallback={closeEditModal}
        />
        {/* 確認モーダル */}
        <ConfirmationModal
          confirmationModalState={confirmationModalState}
          closeCallback={closeConfirmationModal}
        />
      </section>
    </section>
  )
}

export default Index
